// import { useAuth } from "@elit-hair/elit-tools";
import { useMutation, useQuery } from "@tanstack/react-query";

import { treatmentSvcAxiosInstance } from "@/constants";
import { SettingsDefinition } from "@/providers/general/settings-provider";
import { queryClient } from "@/utils/queryClient";

type GetSettingResponse = {
    userId: string;
    configData: SettingsDefinition;
    createdAt: string;
    updatedAt: string;
    id: string;
};

const queryKeys = {
    currentSettings: () => ["current_settings"] as const,
    userConfig: (userId: string) => ["user_config", userId] as const,
};
const endpoints = {
    get: async () => await treatmentSvcAxiosInstance.get<GetSettingResponse>(`/remote-config`),

    update: async (data: Record<string, unknown>) => await treatmentSvcAxiosInstance.patch(`/remote-config`, data),
    reset: async () => await treatmentSvcAxiosInstance.put(`/remote-config/reset`),
    getUserConfig: async (userId: string) => await treatmentSvcAxiosInstance.get(`/remote-config/admin/user/${userId}`),
    updateUserConfig: async (userId: string, data: Record<string, unknown>) =>
        await treatmentSvcAxiosInstance.patch(`/remote-config/admin/user/${userId}`, data),
    resetConfigForUser: async (userId: string) =>
        await treatmentSvcAxiosInstance.put(`/remote-config/admin/user/${userId}/reset`),
    // TODO: Add bulk update for admins
    // bulkUpdate: async (data: Record<string, string>) =>
    //     await callSvcAxiosInstance.patch(`/remote-config/admin/bulk`, data),
};

// --- Current Settings ---
// Get current settings
export const useCurrentSettings = () => {
    // const { auth } = useAuth();

    return useQuery({
        queryKey: queryKeys.currentSettings(),
        queryFn: async () => {
            const response = await endpoints.get();
            return response.data?.configData;
        },

        // enabled: !!auth, // Only run when authenticated
        enabled: false, // TODO: Temporarily disabled until /remote-config endpoint is available
    });
};

// Update current settings
export const useUpdateCurrentConfig = () => {
    return useMutation({
        mutationFn: (data: Record<string, unknown>) => endpoints.update(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.currentSettings() });
        },
    });
};

// Reset current config
export const useResetCurrentConfig = () => {
    return useMutation({
        mutationFn: () => endpoints.reset(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.currentSettings() });
        },
    });
};

// --- Admins ---
// Update user config
export const useGetUserConfig = (userId?: string) => {
    return useQuery({
        queryKey: queryKeys.userConfig(userId || ""),
        queryFn: async () => {
            const response = await endpoints.getUserConfig(userId || "");
            return response.data?.configData;
        },
        enabled: !!userId,
        staleTime: 0,
    });
};

export const useUpdateUserConfig = (userId: string) => {
    return useMutation({
        mutationFn: (data: Record<string, boolean | string | number>) => endpoints.updateUserConfig(userId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.currentSettings() });
        },
    });
};

// Reset config for user
export const useResetConfigForUser = (userId: string) => {
    return useMutation({
        mutationFn: () => endpoints.resetConfigForUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.currentSettings() });
        },
    });
};
